import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import useOnClickOutside from 'use-onclickoutside';

import { useDelay, usePopupPosition } from '../../hooks';

type RefableElement = {
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
} & React.RefAttributes<HTMLElement>;

type OverlayTooltipProps = {
  children: React.ReactElement<RefableElement>;
  tooltip: React.ReactNode;
  delay?: number;
  maxWidth?: number;
};

const OverlayTooltip: React.FC<OverlayTooltipProps> = ({
  children,
  tooltip,
  delay = 200,
  maxWidth = 300,
}) => {
  const [open, setOpen] = useState(false);
  const refAnchorRef = useRef<HTMLElement>(null as any as HTMLElement);

  const {
    popupRef,
    anchorRef,

    popupPosition,
    anchorPosition,
  } = usePopupPosition({ enable: open });

  const handleClose = () => setOpen(false);
  useOnClickOutside(refAnchorRef, handleClose);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    if (children.props.onMouseEnter) children.props.onMouseEnter(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    handleClose();
    if (children.props.onMouseLeave) children.props.onMouseLeave(e);
  };

  const triggerElement = React.cloneElement(
    children as React.ReactElement<RefableElement>,
    {
      ref: (node: HTMLElement) => {
        node && anchorRef(node as HTMLDivElement);
        refAnchorRef.current = node;
      },
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }
  );

  const delayedOpen = useDelay({ delay, value: open, defaultValue: false });

  return (
    <>
      {triggerElement}
      {!!open && (
        <div
          ref={popupRef}
          style={{
            maxWidth,
            top: popupPosition?.style?.top ?? 0,
            minWidth: anchorPosition.width ?? 0,
            left: popupPosition?.style?.left ?? 0,
          }}
          className={clsx('fixed z-popper py-sm')}
        >
          <div
            className={clsx(
              'shadow-sm border bg-content border-active p-xs px-md rounded-md text-small text-text',
              delayedOpen ? 'opacity-100' : 'opacity-0',
              'transition-non-position'
            )}
          >
            {tooltip}
          </div>
        </div>
      )}
    </>
  );
};

export default OverlayTooltip;
